// Native auth via @react-native-firebase/auth, loaded through a guarded
// require so the app never crashes if the module isn't in the build yet
// (same pattern as lib/analytics.ts and lib/ads.ts). Until the dependency is
// installed and the app rebuilt, every method resolves to a clear
// 'not-configured' AuthError and isAvailable() returns false. Web is served
// by index.web.ts (Firebase JS SDK).

import { Platform } from 'react-native';

import { AuthError, type AuthService, type AuthUser, mapAuthError } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */
let mod: any | null = null;
let attempted = false;

const getModule = (): any | null => {
  if (Platform.OS === 'web') return null;
  if (attempted) return mod;
  attempted = true;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    mod = require('@react-native-firebase/auth').default;
  } catch {
    mod = null;
  }
  return mod;
};

const authInstance = (): any | null => {
  const m = getModule();
  return m ? m() : null;
};

const requireAuth = (): any => {
  const a = authInstance();
  if (!a) throw new AuthError('not-configured', '@react-native-firebase/auth is not installed');
  return a;
};

const toUser = (u: any): AuthUser => ({
  uid: u.uid,
  email: u.email ?? null,
  displayName: u.displayName ?? null,
  emailVerified: !!u.emailVerified,
  isAnonymous: !!u.isAnonymous,
  providerId: u.providerData?.[0]?.providerId ?? (u.isAnonymous ? 'anonymous' : 'password'),
});

export const isAvailable = (): boolean => authInstance() !== null;

export const getCurrentUser = (): AuthUser | null => {
  const u = authInstance()?.currentUser;
  return u ? toUser(u) : null;
};

export const onAuthChanged = (cb: (user: AuthUser | null) => void): (() => void) => {
  const a = authInstance();
  if (!a) {
    cb(null);
    return () => {};
  }
  return a.onAuthStateChanged((u: any) => cb(u ? toUser(u) : null));
};

// Link an anonymous account to a credential (keeping its uid + data); fall
// back to a plain sign-in when that provider account already exists, or when
// the current user isn't anonymous.
const linkOrSignIn = async (a: any, credential: any): Promise<AuthUser> => {
  const current = a.currentUser;
  if (current?.isAnonymous) {
    try {
      const { user } = await current.linkWithCredential(credential);
      return toUser(user);
    } catch (e) {
      if ((e as { code?: string })?.code === 'auth/credential-already-in-use') {
        const { user } = await a.signInWithCredential(credential);
        return toUser(user);
      }
      throw e;
    }
  }
  const { user } = await a.signInWithCredential(credential);
  return toUser(user);
};

export const signUpWithEmail = async (email: string, password: string): Promise<AuthUser> => {
  const a = requireAuth();
  const authMod = getModule();
  try {
    // Upgrade an anonymous account in place so its data survives.
    if (a.currentUser?.isAnonymous) {
      const cred = authMod.EmailAuthProvider.credential(email, password);
      const { user } = await a.currentUser.linkWithCredential(cred);
      return toUser(user);
    }
    const { user } = await a.createUserWithEmailAndPassword(email, password);
    return toUser(user);
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const signInWithEmail = async (email: string, password: string): Promise<AuthUser> => {
  try {
    const { user } = await requireAuth().signInWithEmailAndPassword(email, password);
    return toUser(user);
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const signInWithGoogle = async (): Promise<AuthUser> => {
  const a = requireAuth();
  const authMod = getModule();
  let GoogleSignin: any;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    ({ GoogleSignin } = require('@react-native-google-signin/google-signin'));
  } catch {
    throw new AuthError(
      'not-configured',
      'Install @react-native-google-signin/google-signin and set EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID to enable Google sign-in.'
    );
  }
  try {
    GoogleSignin.configure({ webClientId: process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID });
    await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
    const result = await GoogleSignin.signIn();
    // google-signin v13 returns { data: { idToken } }; older returns { idToken }.
    const idToken = result?.data?.idToken ?? result?.idToken;
    if (!idToken) throw new AuthError('unknown', 'No Google idToken returned');
    const credential = authMod.GoogleAuthProvider.credential(idToken);
    return await linkOrSignIn(a, credential);
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const signInWithApple = async (): Promise<AuthUser> => {
  const a = requireAuth();
  const authMod = getModule();
  let AppleAuthentication: any;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    AppleAuthentication = require('expo-apple-authentication');
  } catch {
    throw new AuthError(
      'not-configured',
      'Install expo-apple-authentication to enable Apple sign-in.'
    );
  }
  try {
    const cred = await AppleAuthentication.signInAsync({
      requestedScopes: [
        AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
        AppleAuthentication.AppleAuthenticationScope.EMAIL,
      ],
    });
    if (!cred.identityToken) throw new AuthError('unknown', 'No Apple identityToken returned');
    const credential = authMod.AppleAuthProvider.credential(cred.identityToken, cred.nonce);
    return await linkOrSignIn(a, credential);
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const signInAnonymously = async (): Promise<AuthUser> => {
  try {
    const { user } = await requireAuth().signInAnonymously();
    return toUser(user);
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const sendPasswordReset = async (email: string): Promise<void> => {
  try {
    await requireAuth().sendPasswordResetEmail(email);
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const signOut = async (): Promise<void> => {
  try {
    await requireAuth().signOut();
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const deleteAccount = async (): Promise<void> => {
  try {
    const u = requireAuth().currentUser;
    if (u) await u.delete();
  } catch (e) {
    throw mapAuthError(e);
  }
};

const service: AuthService = {
  isAvailable,
  getCurrentUser,
  onAuthChanged,
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  signInWithApple,
  signInAnonymously,
  sendPasswordReset,
  signOut,
  deleteAccount,
};

export default service;
